import type { FC } from 'react'

import styled from '@emotion/styled'

import Badge from '@segment/matcha/components/Badge'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import { ButtonText } from '@segment/matcha/components/ButtonText/ButtonText'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import CardBase, * as Card from '@segment/matcha/recipes/Card'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

const copy = {
  heading: `Related content`,
}

const cards = [
  {
    badges: [`Recipe`],
    title: `Fill the gaps in your profiles using data from your warehouse`,
    content: `In this recipe, you’ll set up rETL to automatically pull in both backfill customer data and events from your warehouse to help complete your identity graph.`,
    href: `/recipes/fill-gaps-warehouse-data-retl/`,
    buttonText: `Read more`,
  },
  {
    badges: [`Recipe`],
    title: `Use Segment Reverse ETL to sync your Snowflake customer table to Salesforce`,
    content: `In this recipe we’ll explore how to set up this synchronization so that your systems don’t diverge, and you don’t have extra work to keep them that way.`,
    href: `/recipes/segment-retl-sync-snowflake-salesforce/`,
    buttonText: `Read more`,
  },
  {
    badges: [`Recipe`],
    title: `Tie offline conversions and revenue to your ad spend through Google Ads`,
    content: `In this recipe, you’ll set up reverse ETL to automatically pull in conversion events that originated from a Google Ad that are locked away in a Snowflake table or view.`,
    href: `/recipes/tie-offline-conversions-to-ad-spend-google/`,
    buttonText: `Read more`,
  },
]

const RelatedContent: FC = () => {
  const pageConfig = usePageConfig()

  return (
    <StyledSectionBase variant="centered">
      <Section.Copy>
        <CopyTextBase centered={true}>
          <CopyText.Heading tag="h3">{copy.heading}</CopyText.Heading>
        </CopyTextBase>
      </Section.Copy>
      <Section.Contents>
        <ColumnTrack
          columns={1}
          overrides={{
            md: 3,
          }}
        >
          {cards.map(({ badges, title, content, href, buttonText }, key) => (
            <CardBase href={getAbsoluteUrl(href, pageConfig)} key={key}>
              <Card.Body>
                <Card.Accessories>
                  <Card.AccessoriesRow>
                    {badges.map((badge, key) => (
                      <Badge key={key} variant="outlined">
                        {badge}
                      </Badge>
                    ))}
                  </Card.AccessoriesRow>
                </Card.Accessories>
                <Card.Copy>
                  <Card.Headline>{title}</Card.Headline>
                  <Card.Description>{content}</Card.Description>
                </Card.Copy>
                <Card.Action alignment="left">
                  <ButtonText size="medium" iconEnd={ChevronRightIcon}>
                    {buttonText}
                  </ButtonText>
                </Card.Action>
              </Card.Body>
            </CardBase>
          ))}
        </ColumnTrack>
      </Section.Contents>
    </StyledSectionBase>
  )
}

const StyledSectionBase = styled(SectionBase)`
  max-width: 1280px;

  ${Section.Wrapper} {
    padding-bottom: 64px;

    ${({ theme }) => theme.breakpoints.up(`md`)} {
      padding-bottom: 160px;
    }
  }
`

export default RelatedContent
